import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { BRAND, buildWhatsAppSimpleLink } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Veuillez remplir tous les champs requis.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Votre message a bien été envoyé. À très vite !");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#F9F6F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold">Contattaci</p>
        <h1 className="font-heading text-5xl md:text-6xl text-[#1A1617] mt-3">
          Restons en <em className="italic text-[#5B2333]">contact</em>
        </h1>
        <p className="text-base text-[#5C5651] mt-6 max-w-2xl">
          Une question, une commande spéciale, un événement à organiser ? Nous
          répondons rapidement par WhatsApp, email ou via ce formulaire.
        </p>

        <div className="grid lg:grid-cols-12 gap-12 mt-16">
          {/* Coordonnées */}
          <aside className="lg:col-span-4 space-y-8" data-testid="contact-info">
            <InfoRow icon={MapPin} label="Adresse" value={BRAND.address} />
            <InfoRow icon={Phone} label="Téléphone" value={BRAND.phoneDisplay} href={buildWhatsAppSimpleLink()} />
            <InfoRow icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} />

            <a
              href={buildWhatsAppSimpleLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm tracking-[0.18em] uppercase font-semibold hover:opacity-90 transition-opacity"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
              Discuter sur WhatsApp
            </a>
          </aside>

          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-8 bg-white border border-[#E8E2D2] p-8 md:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nom" required>
                <input value={form.name} onChange={update("name")} required data-testid="contact-name" className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]" />
              </Field>
              <Field label="Téléphone">
                <input value={form.phone} onChange={update("phone")} data-testid="contact-phone" className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]" />
              </Field>
            </div>
            <Field label="Email" required>
              <input type="email" value={form.email} onChange={update("email")} required data-testid="contact-email" className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]" />
            </Field>
            <Field label="Message" required>
              <textarea value={form.message} onChange={update("message")} rows={5} required data-testid="contact-message" className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617] resize-none" />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 bg-[#5B2333] text-white px-8 py-4 text-sm tracking-[0.18em] uppercase font-semibold hover:bg-[#4A1525] transition-colors disabled:opacity-60"
            >
              <Send className="w-4 h-4" strokeWidth={1.8} />
              {loading ? "Envoi…" : "Envoyer le message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, href }) => {
  const content = (
    <div className="flex items-start gap-4">
      <span className="w-10 h-10 rounded-full bg-[#5B2333]/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#5B2333]" strokeWidth={1.5} />
      </span>
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold">{label}</p>
        <p className="text-sm text-[#1A1617] mt-1">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block hover:opacity-80 transition-opacity">{content}</a>
  ) : content;
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#C5A059] font-semibold">
      {label}{required && <span className="text-[#5B2333]"> *</span>}
    </span>
    {children}
  </label>
);

export default Contact;
